/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Landing route. Unauthenticated users are sent to Auth0 login.
 * Authenticated users without a paid tier are logged out to farelanes.com/shop.
 */

import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate } from "react-router-dom";
import { PageLayout } from "../components/PageLayout";
import { PageLoader } from "../components/PageLoader";
import { ROUTES } from "../config/routes";
import UserService from "../services/UserService";
import {
  hasActivePaidSubscription,
  logoutToShop,
  waitForPaidSubscription,
} from "../utils/subscriptionGate";
import { consumeSignedOutReason } from "../utils/sessionManager";

const SIGNED_OUT_MESSAGES = {
  SESSION_SUPERSEDED:
    "You were signed out because this account was used on another device. Only one device can be signed in at a time.",
  SESSION_EXPIRED: "Your session expired. Please sign in again.",
  SESSION_REQUIRED: "Your session could not be verified. Please sign in again.",
};

export const HomePage = () => {
  const {
    isAuthenticated,
    isLoading: authLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    user,
    logout,
  } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const [signedOutMessage, setSignedOutMessage] = useState(null);

  useEffect(() => {
    const reason = consumeSignedOutReason();
    if (reason) {
      setSignedOutMessage(SIGNED_OUT_MESSAGES[reason] || SIGNED_OUT_MESSAGES.SESSION_EXPIRED);
    }
  }, []);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const verified = searchParams.get("verified");

    if (verified === "true" && !isAuthenticated) {
      loginWithRedirect({
        appState: {
          returnTo: ROUTES.DASHBOARD,
        },
        authorizationParams: {
          prompt: "login",
        },
      });
      return;
    }

    if (!isAuthenticated) {
      loginWithRedirect({
        appState: {
          returnTo: ROUTES.DASHBOARD,
        },
      });
      return;
    }

    if (user) {
      let cancelled = false;

      (async () => {
        try {
          const token = await getAccessTokenSilently();
          const sub = await waitForPaidSubscription(() =>
            UserService.getUserSubscription(token, user, true)
          );
          if (cancelled) {
            return;
          }

          if (hasActivePaidSubscription(sub)) {
            if (location.pathname !== ROUTES.DASHBOARD) {
              navigate(ROUTES.DASHBOARD, { replace: true });
            }
            return;
          }

          logoutToShop(logout);
        } catch (e) {
          console.error(
            "HomePage: Error checking subscription, logging out to shop:",
            e
          );
          if (!cancelled) {
            logoutToShop(logout);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }
  }, [
    authLoading,
    location.search,
    location.pathname,
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
    user,
    navigate,
    logout,
  ]);

  return (
    <PageLayout>
      {signedOutMessage && (
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="alert alert-warning text-center" role="alert" style={{ marginTop: "16px" }}>
                {signedOutMessage}
              </div>
            </div>
          </div>
        </div>
      )}
      <PageLoader />
    </PageLayout>
  );
};
